import { Component, OnInit, OnDestroy } from '@angular/core';
import { faEdit, faRefresh, faBuilding, faUser, faPhone, faEnvelope, faLocationDot, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Subject, interval } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserCache } from "../services/user/user.cache";
import { UserManager } from "../services/user/user.manager";

/**
 * This component provides an overview of information for the logged-in user.
 */
@Component({
	selector: 'user-overview',
	templateUrl: './user-overview.component.html',
	styleUrl: './user-overview.component.scss'
})
export class UserOverviewComponent implements OnInit, OnDestroy {
	/**
	 * A subject that can be used to automatically end subscriptions when the component is destroyed.
	 * @private
	 */
	private readonly _destroying$ = new Subject<void>();

	// Icon definitions.
	public faEdit = faEdit;
	public faRefresh = faRefresh;
	public faBuilding = faBuilding;
	public faUser = faUser;
	public faPhone = faPhone;
	public faEmail = faEnvelope;
	public faAddress = faLocationDot;
	public faCircleInfo = faCircleInfo;
	
	public rfUpdateTooltip : string = "To update this information, please contact our support team at 1-800-531-1925. Our representatives are available Mon-Fri 6 AM-5 PM MST, Sat 7 AM-2 PM MST to assist you. Thank you!";
	public rfUpdateTooltipDelay : number = 1000;

	// Public properties for template binding (replacing getters)
	public showRepairFacility: boolean = false;
	public showContact: boolean = false;

	/**
	 * @constructor
	 * @param userManager The {@link UserManager} to use for updates.
	 * @param userCache The {@link UserCache} to use for user data.
	 */
	constructor(public userManager: UserManager,
				public userCache: UserCache) {
	}

	ngOnInit(): void {
		// Initialize property values
		this.updateVisibilityProperties();

		// Subscribe to user cache changes
		this.userCache.updated
			.pipe(takeUntil(this._destroying$))
			.subscribe(() => {
				this.updateVisibilityProperties();
			});

		// Set up periodic polling to check for loading state changes
		// This is needed because userManager flags change asynchronously
		interval(100)
			.pipe(takeUntil(this._destroying$))
			.subscribe(() => {
				const previousShowRepairFacility = this.showRepairFacility;
				const previousShowContact = this.showContact;
				
				this.updateVisibilityProperties();
				
				// No need to trigger change detection since these are simple property updates
				// Angular's default change detection will pick them up
			});
	}

	/**
	 * Update the visibility properties when dependent values change.
	 */
	private updateVisibilityProperties(): void {
		this.showRepairFacility = this.userManager.updatingRepairFacilities || this.userCache.selectedRepairFacility != undefined;
		this.showContact = this.userManager.updatingContacts || this.userCache.selectedContact != undefined;
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}
}
